/**
 * API Retry Logic with Exponential Backoff
 * Handles transient network failures gracefully
 */

import { logAPI } from './logger';

export interface RetryOptions {
  maxRetries?: number;
  baseDelayMs?: number;
  maxDelayMs?: number;
  retryableStatuses?: number[];
  onRetry?: (attempt: number, error: Error) => void;
}

const DEFAULT_OPTIONS: Required<RetryOptions> = {
  maxRetries: 3,
  baseDelayMs: 1000,
  maxDelayMs: 10000,
  retryableStatuses: [408, 429, 500, 502, 503, 504], // Timeout, Rate limit, Server errors
  onRetry: () => {},
};

/**
 * Determines if an error should be retried
 */
function shouldRetry(error: unknown, attempt: number, options: Required<RetryOptions>): boolean {
  if (attempt >= options.maxRetries) {
    return false;
  }

  // Retry network errors (status 0 indicates network failure)
  if (error instanceof Error && 'status' in error) {
    const status = (error as any).status;

    // Always retry network failures (status 0)
    if (status === 0) {
      return true;
    }

    // Retry specific HTTP status codes
    return options.retryableStatuses.includes(status);
  }

  // Retry other network errors (fetch failures, etc.)
  if (error instanceof TypeError && error.message.includes('fetch')) {
    return true;
  }

  return false;
}

/**
 * Calculate delay with exponential backoff and jitter
 */
function getRetryDelay(attempt: number, options: Required<RetryOptions>): number {
  // Exponential backoff: baseDelay * 2^attempt
  const exponentialDelay = options.baseDelayMs * Math.pow(2, attempt);

  // Add jitter (randomness) to prevent thundering herd
  const jitter = Math.random() * 0.3 * exponentialDelay;

  const delay = exponentialDelay + jitter;

  return Math.min(delay, options.maxDelayMs);
}

/**
 * Sleep for specified milliseconds
 */
function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Retry a function with exponential backoff
 *
 * @example
 * ```typescript
 * const data = await withRetry(
 *   () => apiRequest('/cases'),
 *   {
 *     maxRetries: 3,
 *     onRetry: (attempt) => console.log(`Retry attempt ${attempt}`)
 *   }
 * );
 * ```
 */
export async function withRetry<T>(
  fn: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> {
  const opts: Required<RetryOptions> = { ...DEFAULT_OPTIONS, ...options };

  let lastError: unknown;

  for (let attempt = 0; attempt <= opts.maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;

      if (!shouldRetry(error, attempt, opts)) {
        logAPI.debug('Not retrying', { attempt, error });
        throw error;
      }

      const delay = getRetryDelay(attempt, opts);

      logAPI.warn('API call failed, retrying', {
        attempt: attempt + 1,
        maxRetries: opts.maxRetries,
        delayMs: delay,
        error: error instanceof Error ? error.message : String(error),
      });

      opts.onRetry(attempt + 1, error as Error);

      await sleep(delay);
    }
  }

  // All retries exhausted
  logAPI.error('All retry attempts exhausted', { lastError });
  throw lastError;
}

/**
 * Check if the browser is currently online
 */
export function isOnline(): boolean {
  return typeof navigator !== 'undefined' ? navigator.onLine : true;
}

/**
 * Wait for the browser to come back online
 */
export function waitForOnline(timeoutMs: number = 30000): Promise<void> {
  return new Promise((resolve, reject) => {
    if (isOnline()) {
      resolve();
      return;
    }

    const timeout = setTimeout(() => {
      cleanup();
      reject(new Error('Timeout waiting for network connection'));
    }, timeoutMs);

    const handleOnline = () => {
      cleanup();
      resolve();
    };

    const cleanup = () => {
      clearTimeout(timeout);
      window.removeEventListener('online', handleOnline);
    };

    window.addEventListener('online', handleOnline);
  });
}
